import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TableAction, TableColumn, TVChannel } from '@/types';
import { Edit, Eye, Plus, Search, Trash2 } from 'lucide-react';
import { useConfirmDialog, useTranslation, useCategoriesForDropdown, useCountriesList } from '@/hooks';
import toast from 'react-hot-toast';
import {
  useTVChannels,
  useDeleteTVChannel,
  useToggleTVChannelStatus,
} from '@/hooks';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import { useMemo, useState } from 'react';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import { useBranding } from '@/context/BrandingContext';
import StreamPlayerButton from '@/components/ui/StreamPlayerButton';

const TVChannels = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const { branding } = useBranding();
  const showCountryField = branding.modules?.liveTvCountriesEnabled === true;

  const [categorySearch, setCategorySearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const selectedCategoryId = searchParams.get('categories') ?? '';
  const selectedCountryId = searchParams.get('country.id') ?? '';

  const { data: categories } = useCategoriesForDropdown('TV_CHANNEL');
  const { data: countries } = useCountriesList();

  const getRawStreamUrl = (channel: TVChannel) => {
    const playUrl = typeof channel.playUrl === 'string' ? channel.playUrl : '';
    const fallbackUrl = typeof channel.url === 'string' ? channel.url : '';
    return (playUrl || fallbackUrl).trim();
  };

  // Mutations
  const deleteTVChannelMutation = useDeleteTVChannel();
  const toggleStatusMutation = useToggleTVChannelStatus();

  const tvChannelColumns: TableColumn<TVChannel>[] = [
    {
      sortable: true,
      key: 'channelNumber',
      header: "N°",
      align: "center",
      className: "w-6",
      accessor: (channel) => (
        <div className="flex items-center mx-auto p-0">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {channel.channelNumber}
          </span>
        </div>
      )
    },
    {
      key: 'imageUrl',
      header: t.tvChannels.fields.logo,
      className: 'w-8',
      accessor: (channel) => (
        <div className="h-6 w-auto flex items-center justify-center">
          {channel.imageUrl ? (
          <img
            src={channel.imageUrl}
            alt={`logo`}
            className="h-6 object-cover mx-auto"
          />
          ) : (
          <div className="bg-gray-200 rounded ">
            <span className="text-gray-500 text-sm">N/A</span>
          </div>
          )}
        </div>
      ),
    },
    {
      sortable: true,
      key: 'title',
      header: t.tvChannels.fields.title,
      accessor: (channel) => (
        <div className="font-medium text-gray-900 cursor-pointer">
          <Link
            className="link cursor-pointe"
            to={`/content/tv-channels/${channel.id}/edit`}>
            {channel.title}
          </Link>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      sortable: true,
      key: "url",
      header: "URL",
      className: "max-w-50 overflow-hidden text-ellipsis",
    },
    ...(showCountryField
      ? [{
          sortable: true,
          key: 'country.id',
          className: 'w-8',
          align: 'center',
          header: t.tvChannels.fields.country,
          accessor: (channel: TVChannel) => (
            <div className="text-gray-700">
              {channel.country?.flag && (
                /^https?:\/\//.test(channel.country.flag) ? (
                  <img
                    src={channel.country.flag}
                    alt={`${channel.country.name} flag`}
                    className="inline-block h-5 w-8 object-cover mr-2"
                  />
                ) : (
                  <span className="inline-block text-lg mr-2" aria-hidden="true">
                    {channel.country.flag}
                  </span>
                )
              )}
            </div>
          )
        } satisfies TableColumn<TVChannel>]
      : []),
    {
      key: "categoryNames",
      align: 'center',
      header: t.tvChannels.fields.categories,
      accessor: (channel) => (
        <div className="flex flex-wrap gap-1 justify-center">
          {channel.categoryNames.map((category, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {category}
            </span>
          ))}
        </div>
      )
    },
    {
      sortable: true,
      key: 'active',
      className: 'w-16',
      align: 'center',
      header: t.tvChannels.fields.active,
      accessor: (channel) => (
        <Toggle
          checked={channel.active}
          onChange={(checked) => handleToggleStatus(channel.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    },
    {
      key: 'play',
      header: "",
      className: 'w-5',
      align: 'center',
      accessor: (channel) => (
        <StreamPlayerButton
          title={channel.title}
          streamUrl={getRawStreamUrl(channel)}
          playLabel={t.tvChannels.actions.play}
          noPlayableStreamMessage={t.tvChannels.messages.noPlayableStream}
          rtspInlineNotSupportedMessage={t.tvChannels.messages.rtspInlineNotSupported}
        />
      )
    }
  ];

  const tvChannelActions: TableAction<TVChannel>[] = [
    {
      label: t.tvChannels.actions.edit,
      icon: Edit,
      onClick: (channel) => navigate(`/content/tv-channels/${channel.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: t.tvChannels.actions.activate,
      icon: Eye,
      onClick: (channel) => handleToggleStatus(channel.id, !channel.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (channel) => !channel.active
    },
    {
      label: t.tvChannels.actions.deactivate,
      icon: Eye,
      onClick: (channel) => handleToggleStatus(channel.id, !channel.active),
      className: 'text-gray-700 hover:text-yellow-600',
      show: (channel) => channel.active
    },
    {
      label: t.tvChannels.actions.delete,
      icon: Trash2,
      onClick: (channel) => handleDeleteTVChannel(channel.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((category) => ({
        value: category.id,
        label: category.description || category.englishDescription,
      })),
    [categories]
  );

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((country) => ({
        value: country.id,
        label: country.nameEnglish || country.name,
      })),
    [countries]
  );

  const filteredCategoryOptions = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return categoryOptions;
    return categoryOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [categoryOptions, categorySearch]);

  const filteredCountryOptions = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return countryOptions;
    return countryOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [countryOptions, countrySearch]);

  const initialFilters = useMemo(
    () => ({
      categories: selectedCategoryId || undefined,
      ...(showCountryField ? { 'country.id': selectedCountryId || undefined } : {}),
    }),
    [selectedCategoryId, selectedCountryId, showCountryField]
  );

  const updateFilterParam = (key: 'categories' | 'country.id', value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }

    setSearchParams(nextParams);
  };

  const handleCreateTVChannel = () => {
    navigate('/content/tv-channels/create');
  };

  const handleDeleteTVChannel = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.tvChannels.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteTVChannelMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      toast.success(t.tvChannels.messages.deleted);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      toast.success(t.tvChannels.messages.statusUpdated);
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  return (
    <TVChannelsLayout>
      <div className="w-full">
        <DataTableWithQuery
          tableStyle="compact"
          columns={tvChannelColumns}
          actions={tvChannelActions}
          useQueryHook={useTVChannels}
          headerRenderer={({ title, searchQuery, onSearchChange, searchPlaceholder }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                  <Button onClick={handleCreateTVChannel} size="sm">
                    <Plus className="mr-2 h-4 w-4"/>
                    Nuevo canal
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <AutocompleteSelect
                      value={selectedCategoryId}
                      onChange={(value) => {
                        updateFilterParam('categories', value);
                        setCategorySearch('');
                      }}
                      onSearch={setCategorySearch}
                      options={filteredCategoryOptions}
                      placeholder="Todas las categorias"
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                  {showCountryField && (
                    <div className="min-w-0">
                      <AutocompleteSelect
                        value={selectedCountryId}
                        onChange={(value) => {
                          updateFilterParam('country.id', value);
                          setCountrySearch('');
                        }}
                        onSearch={setCountrySearch}
                        options={filteredCountryOptions}
                        placeholder="Todos los paises"
                        size="sm"
                        className="text-base"
                        allowClear
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={selectedCategoryId}
                    onChange={(value) => {
                      updateFilterParam('categories', value);
                      setCategorySearch('');
                    }}
                    onSearch={setCategorySearch}
                    options={filteredCategoryOptions}
                    placeholder="Todas las categorias"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                {showCountryField && (
                  <div className="min-w-[180px]">
                    <AutocompleteSelect
                      value={selectedCountryId}
                      onChange={(value) => {
                        updateFilterParam('country.id', value);
                        setCountrySearch('');
                      }}
                      onSearch={setCountrySearch}
                      options={filteredCountryOptions}
                      placeholder="Todos los paises"
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                )}
                <div className="ml-auto">
                  <Button onClick={handleCreateTVChannel} size="sm">
                    <Plus className="mr-2 h-4 w-4"/>
                    Nuevo canal
                  </Button>
                </div>
              </div>
            </div>
          )}
          searchPlaceholder={t.tvChannels.placeholders.search}
          title={t.tvChannels.listTitle}
          itemsPerPage={20}
          initialFilters={initialFilters}
          className="rounded-none shadow-none border border-gray-200"
        />
        {dialog}
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannels;
